import { Component, HostBinding, QueryList, ViewChildren } from '@angular/core';
import { SelectionChangedEvent } from 'ag-grid-community';
import { Corpus } from '../../../../models/corpus';
import { Endpoint, HelpIndex, UpdateStatusType } from '../../../../models/enums';
import { CellTemplateType, GenericColDef, GridContext, GridRowSelectionType, SortByFilter } from '../../../../models/grid-context';
import { SettingsNavigationContentComponent } from '../../../../models/settings-navigation-content-component';
import { CommonService } from '../../../../services/common/common.service';
import { RestService } from '../../../../services/rest/rest.service';
import { GenericGridComponent } from '../../../generic-grid/generic-grid.component';
import { EditSharedCorpusModalComponent } from '../../edit-shared-corpus-modal/edit-shared-corpus-modal.component';
import { ContextMenuOption } from '../../../../models/context-menu-option';

@Component({
    standalone: false,
    selector: 'res-shared-data-sets',
    templateUrl: './shared-data-sets.component.html',
    styleUrls: ['./shared-data-sets.component.scss']
})
export class SharedDataSetsComponent implements SettingsNavigationContentComponent {
    @HostBinding('class') classes = 'maximized-component';
    @ViewChildren(GenericGridComponent) genericGrids: QueryList<GenericGridComponent>;

    isDirty: boolean;
    validChanges = true;
    selectedCorpus: Corpus;

    gridName = 'Shared Data Sets';
    automationId = 'shared-data-sets';
    automationIdDataSetProjects = 'shared-data-set-projects';
    helpIndex = HelpIndex.shared_datasets_org_manage_dlg;
    updateStatusType = UpdateStatusType.CORPUS;

    columnDefs = new Array<GenericColDef>(
        {
            headerName: 'Data Set Name',
            field: 'name',
            automationId: this.automationId,
            sortBy: SortByFilter.NAME.toString(),
            lockVisible: true
        },
        {
            headerName: 'Data Set Description',
            field: 'description',
            automationId: this.automationId
        },
        {
            headerName: 'Originating Project Name',
            field: 'owner',
            automationId: this.automationId
        },
        {
            headerName: 'Originating Project Description',
            field: 'ownerDescription',
            automationId: this.automationId
        },
        {
            automationId: this.automationId,
            cellTemplateType: CellTemplateType.contextMenu,
            minWidth: 30, maxWidth: 30, width: 30,
            suppressSizeToFit: true
        }
    );
    columnDefsDataSetProjects = new Array<GenericColDef>(
        {
            headerName: 'Attached Project Name',
            field: 'name',
            automationId: this.automationIdDataSetProjects,
            sortBy: SortByFilter.NAME.toLowerCase().toString(),
            lockVisible: true
        },
        {
            headerName: 'Attached Project Description',
            field: 'description',
            automationId: this.automationIdDataSetProjects
        },
    );
    rowSelection = GridRowSelectionType.single;
    contextMenuOptions = [new ContextMenuOption({
        name: 'Edit...', iconClass: 'fa-pencil', iconColor: 'color-blue-01', automationId: 'org-settings-corpus-list-edit-corpus',
        click: (item) => this.editSharedCorpus(item),
        enabled: (item) => item.attachedProjects.length > 0
    })];

    requestInfo = {
        endpoint: Endpoint.projectManager_listSharedCorpora,
        responseName: 'corpora',
        data: null
    };
    gridContext = new GridContext({
        sortBy: SortByFilter.NAME.toString(),
        sortAscending: true,
        sortEnabled: false
    });

    editSharedCorpus(item) {
        this.commonService.openModal(EditSharedCorpusModalComponent, {
            backdrop: false, modalData: {
                handle: item.handle, attachedProjects: item.attachedProjects, corpusViewHandle: item.corpusViewHandle, corpusName: item.name
            }
        });
    }

    responseArrayCallback = (value: any): Corpus => {
        const corpus = new Corpus(value);
        corpus.attachedProjects = new Array<{ name: string, description: string }>();
        const attachedProjects = corpus.attributes.filter(a => a.name === 'attachedProjects');
        if (attachedProjects.length > 0) {
            attachedProjects[0].value.split(',').forEach(element => {
                const prjName = element.substr(0, element.indexOf('|'));
                const prjDesc = element.substr(element.indexOf('|') + 1, element.length - 1);
                if (value.owner !== prjName) {
                    corpus.attachedProjects.push({ name: prjName, description: prjDesc });
                }
            });
        }
        return corpus;
    }
    onSelectionChanged = (event?: SelectionChangedEvent): void => {
        if (event && event.api && this.genericGrids.last.gridApi) {
            this.selectedCorpus = event.api.getSelectedRows()[0] as Corpus;
            if (this.selectedCorpus) {
                this.genericGrids.last.setData(this.selectedCorpus.attachedProjects);
            } else {
                this.genericGrids.last.setData([]);
            }
        }
    }

    constructor(private commonService: CommonService,
        private restService: RestService) {
    }

    selectedTreeItemChanged = (): void => {
        this.requestInfo.data = {};
    }

    saveChanges(): Promise<boolean> { return Promise.resolve(true); }

    openHelp = (): void => this.commonService.openHelp(this.helpIndex);
}
